import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { anchorReferenceFromElement } from '@components/ui/HoverPopover/HoverPopover.types';

import { BooleanEditorPanel } from './editors/BooleanEditorPanel';
import { DateRangeEditorPanel } from './editors/DateRangeEditorPanel';
import { MultiSelectEditorPanel } from './editors/MultiSelectEditorPanel';
import { FilterFieldList } from './FilterFieldList';
import { useFilterMenuTokens } from './filterMenuTokens';
import { FilterPanelSurface } from './FilterPanelSurface';
import type { FilterFieldDef, FiltersState, FilterValue } from './types';
import { useCascade } from './useCascade';

const ROOT_PANEL_WIDTH = 240;
const EDITOR_PANEL_WIDTH = 280;

export interface FilterMenuPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  fields: FilterFieldDef[];
  /** Applied filters — fully controlled; the host owns this state. */
  value: FiltersState;
  onChange: (next: FiltersState) => void;
  /**
   * Open directly into this field's editor, anchored to `anchorEl` (chip-edit
   * mode) — the root type list is skipped entirely.
   */
  initialFieldId?: string;
  /** Base z-index; the editor panel renders at +1. Defaults to the theme modal level. */
  zIndex?: number;
}

/**
 * Linear-style cascading filter menu: a root panel listing filter types
 * (searchable), with each type's value editor opening in a second panel
 * beside it on hover or click. See AppliedFilterChips for the chips row and
 * useFilterMenuState for host wiring.
 */
export const FilterMenuPopover: React.FC<FilterMenuPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  fields,
  value,
  onChange,
  initialFieldId,
  zIndex,
}) => {
  const tokens = useFilterMenuTokens();
  const baseZIndex = zIndex ?? tokens.zIndex;
  const editorOnly = initialFieldId !== undefined;

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const cascade = useCascade();
  const rootInputRef = useRef<HTMLInputElement>(null);
  const rootElRef = useRef<HTMLElement | null>(null);
  const subElRef = useRef<HTMLElement | null>(null);

  // Reset transient UI state whenever the menu closes.
  const { closeSub } = cascade;
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
      closeSub();
    }
  }, [open, closeSub]);

  const visibleFields = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? fields.filter(field => field.label.toLowerCase().includes(needle)) : fields;
  }, [fields, query]);

  const activeFieldId = editorOnly ? initialFieldId : (cascade.state?.fieldId ?? null);
  const activeDef = activeFieldId
    ? (fields.find(field => field.id === activeFieldId) ?? null)
    : null;

  const rootReference = useMemo(
    () => (anchorEl ? anchorReferenceFromElement(anchorEl) : null),
    [anchorEl],
  );
  const subReference = editorOnly ? rootReference : (cascade.state?.reference ?? null);
  const isAnchorPress = useCallback(
    (target: EventTarget | null) => target instanceof Node && Boolean(anchorEl?.contains(target)),
    [anchorEl],
  );

  // No Modal manages focus for us — hand it back to the trigger explicitly.
  const requestClose = useCallback(() => {
    onClose();
    anchorEl?.focus();
  }, [onClose, anchorEl]);

  const closeEditor = useCallback(() => {
    if (editorOnly) {
      requestClose();
      return;
    }
    closeSub();
    rootInputRef.current?.focus();
  }, [editorOnly, requestClose, closeSub]);

  const setFieldValue = (fieldId: string, next: FilterValue | null) => {
    if (next === null) {
      onChange(Object.fromEntries(Object.entries(value).filter(([key]) => key !== fieldId)));
      return;
    }
    onChange({ ...value, [fieldId]: next });
  };

  // Switch-display booleans: on = applied with value true, off = removed.
  const handleToggleSwitch = (fieldId: string) => {
    const current = value[fieldId];
    const isOn = current?.kind === 'boolean' && current.value;
    setFieldValue(fieldId, isOn ? null : { kind: 'boolean', value: true });
  };

  const handleToggleOption = (fieldId: string, optionValue: string) => {
    const current = value[fieldId];
    const selected = current?.kind === 'multiSelect' ? current.values : [];
    const next = selected.includes(optionValue)
      ? selected.filter(entry => entry !== optionValue)
      : [...selected, optionValue];
    // Unchecking the last option removes the filter (its chip disappears).
    setFieldValue(fieldId, next.length === 0 ? null : { kind: 'multiSelect', values: next });
  };

  const renderEditor = (def: FilterFieldDef) => {
    const autoFocus = editorOnly || cascade.state?.openedBy === 'explicit';
    const current = value[def.id];
    switch (def.type) {
      case 'multiSelect':
        return (
          <MultiSelectEditorPanel
            def={def}
            selected={current?.kind === 'multiSelect' ? current.values : []}
            onToggle={optionValue => handleToggleOption(def.id, optionValue)}
            onClear={() => setFieldValue(def.id, null)}
            autoFocusSearch={autoFocus}
            onNavigateBack={editorOnly ? undefined : closeEditor}
          />
        );
      case 'boolean':
        return (
          <BooleanEditorPanel
            def={def}
            value={current?.kind === 'boolean' ? current.value : undefined}
            onSelect={next => {
              setFieldValue(def.id, { kind: 'boolean', value: next });
              requestClose();
            }}
            autoFocus={autoFocus}
          />
        );
      case 'dateRange':
        return (
          <DateRangeEditorPanel
            value={current?.kind === 'dateRange' ? current : undefined}
            onSelectPreset={range => {
              setFieldValue(def.id, { kind: 'dateRange', ...range });
              requestClose();
            }}
            onChangeCustom={range =>
              setFieldValue(
                def.id,
                range.start === null && range.end === null ? null : { kind: 'dateRange', ...range },
              )
            }
            autoFocus={autoFocus}
            popupZIndex={baseZIndex + 2}
          />
        );
      case 'custom':
        return def.renderEditor({
          autoFocus,
          value: current,
          onChange: next => setFieldValue(def.id, next),
          onClose: requestClose,
        });
    }
  };

  return (
    <>
      {!editorOnly && (
        <FilterPanelSurface
          open={open}
          anchorReference={rootReference}
          onClose={requestClose}
          placement="bottom-start"
          fallbackPlacements={['top-start']}
          escapeKeyEnabled={cascade.state === null}
          isPressInside={target =>
            isAnchorPress(target) ||
            (target instanceof Node && Boolean(subElRef.current?.contains(target)))
          }
          onElementChange={element => {
            rootElRef.current = element;
          }}
          width={ROOT_PANEL_WIDTH}
          zIndex={baseZIndex}
          ariaLabel="Add filter"
        >
          <FilterFieldList
            fields={visibleFields}
            value={value}
            query={query}
            onQueryChange={nextQuery => {
              setQuery(nextQuery);
              setActiveIndex(0);
            }}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            openFieldId={cascade.state?.fieldId ?? null}
            onRowEnter={cascade.onRowEnter}
            onRowLeave={cascade.onRowLeave}
            onRowOpen={cascade.openNow}
            onToggleSwitch={handleToggleSwitch}
            inputRef={rootInputRef}
          />
        </FilterPanelSurface>
      )}

      {activeDef && (
        <FilterPanelSurface
          // Remount per field so editor-local state (search text) never leaks
          // across a row swap.
          key={activeDef.id}
          open={open && subReference !== null}
          anchorReference={subReference}
          onClose={closeEditor}
          placement={editorOnly ? 'bottom-start' : 'right-start'}
          fallbackPlacements={editorOnly ? ['top-start'] : ['left-start']}
          isPressInside={
            editorOnly
              ? isAnchorPress
              : target =>
                  isAnchorPress(target) ||
                  (target instanceof Node && Boolean(rootElRef.current?.contains(target)))
          }
          onPointerEnter={editorOnly ? undefined : cascade.onSubEnter}
          onPointerLeave={editorOnly ? undefined : cascade.onSubLeave}
          onElementChange={element => {
            subElRef.current = element;
          }}
          width={EDITOR_PANEL_WIDTH}
          zIndex={baseZIndex + 1}
          ariaLabel={`Filter by ${activeDef.label}`}
        >
          {renderEditor(activeDef)}
        </FilterPanelSurface>
      )}
    </>
  );
};
